import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {useQuery} from '@github-ui/react-query'
import {IssueHeaderRefetchServerQuery} from '@github-ui/server-persisted-queries'
import type {IssueHeaderRefetchServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueHeaderRefetchServerQuery'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {createContext, use, useMemo, useState} from 'react'

import type {HeaderFragment, HeaderSecondaryFragment} from './Header'

export function issueHeaderRefetchQueryKey(id: string) {
  return ['issue-header-refetch', id] as const
}

const LIVE_UPDATE_THROTTLE_MS = 250

type IssueHeaderAliveNotification = {
  wait?: number
  issue_title_updated?: true
  issue_state_updated?: true
  issue_transfer_state_updated?: true
}

/** Mirrors the header keys the backend publishes; a ping carrying none of them belongs elsewhere on the page. */
const HEADER_UPDATE_KEYS = [
  'issue_title_updated',
  'issue_state_updated',
  'issue_transfer_state_updated',
] as const satisfies ReadonlyArray<keyof IssueHeaderAliveNotification>

function isHeaderUpdated(data: IssueHeaderAliveNotification): boolean {
  return HEADER_UPDATE_KEYS.some(key => data[key] === true)
}

/** The subset of the header the endpoint returns, so it is merged over the prop rather than replacing it. */
export type IssueHeaderRefetchData = Pick<
  HeaderFragment,
  'id' | 'number' | 'title' | 'titleHTML' | 'url' | 'state' | 'stateReason' | 'viewerCanUpdateNext' | 'duplicateOf'
> & {
  readonly isTransferInProgress: boolean
  readonly repository: Pick<
    HeaderFragment['repository'],
    'id' | 'name' | 'nameWithOwner' | 'isArchived' | 'isWritable' | 'owner'
  >
}

/**
 * `source`: query meta cannot tell an endpoint answer from a `setQueryData` write, which is what
 * optimistic settlement does, so `isFetched` and friends are unusable here.
 * `fetchedBy`: the cache outlives a mount and no pings arrive while unmounted, so a mount trusts
 * only the payload its own fetch produced.
 */
type IssueHeaderRefetchResult = {
  issue?: IssueHeaderRefetchData
  source: 'seed' | 'refetch'
  fetchedBy?: number
}

let nextHeaderMountId = 0

async function fetchIssueHeader(
  id: string,
  fetchedBy: number,
  signal?: AbortSignal,
): Promise<IssueHeaderRefetchResult> {
  const params = new URLSearchParams({id, queryId: IssueHeaderRefetchServerQuery})
  const response = await reactFetchJSON(`/issues/issue-header-refetch.json?${params}`, {signal})
  if (!response.ok) throw new Error(`Issue header refetch failed (${response.status})`)

  const json = (await response.json()) as {
    data?: IssueHeaderRefetchServerQueryQuery & {
      node?: IssueHeaderRefetchData & {__typename: 'Issue'}
    }
    errors?: unknown[]
  }
  if (!json.data) throw new Error('Issue header refetch query returned no data')

  const node = json.data.node
  return {issue: node?.__typename === 'Issue' ? node : undefined, source: 'refetch', fetchedBy}
}

/**
 * `undefined` until the endpoint answers, which is most of a page's life: the header only refetches
 * after an Alive event. Consumers read their page-load data until then, and must never start a
 * second owner, because the mount tag below only trusts the fetch its own owner produced.
 */
export const IssueHeaderDataContext = createContext<IssueHeaderRefetchData | undefined>(undefined)

export function useIssueHeaderDataContext(): IssueHeaderRefetchData | undefined {
  return use(IssueHeaderDataContext)
}

/**
 * Owns the narrow header endpoint. Call this once per page: the sticky header and the header read
 * the same answer through the context, so a state or title change costs one request between them.
 */
export function useIssueHeaderRefetch({
  issue,
  headerSecondary,
  issueUpdatesChannel,
}: {
  issue: HeaderFragment
  headerSecondary?: HeaderSecondaryFragment
  issueUpdatesChannel?: string | null
}): IssueHeaderRefetchData | undefined {
  const issueId = issue.id
  const [headerMountId] = useState(() => ++nextHeaderMountId)
  const headerQueryKey = useMemo(() => issueHeaderRefetchQueryKey(issueId), [issueId])
  useInvalidateOnAliveUpdate<IssueHeaderAliveNotification>(issueUpdatesChannel, headerQueryKey, {
    filter: isHeaderUpdated,
    throttleTimeout: LIVE_UPDATE_THROTTLE_MS,
  })
  // If the secondary data is not available, we default to false
  const secondaryIsTransferInProgress = headerSecondary?.isTransferInProgress ?? false
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- Seeded from the parent; this existing SPQ has no runtime schema and refreshes only after an Alive event.
  const headerQuery = useQuery({
    // Keying on it would give every mount its own entry, so Alive pings would never reach this query.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Deliberately not in the key.
    queryKey: headerQueryKey,
    queryFn: ({signal}) => fetchIssueHeader(issueId, headerMountId, signal),
    initialData: (): IssueHeaderRefetchResult => ({
      issue: {...issue, isTransferInProgress: secondaryIsTransferInProgress},
      source: 'seed',
    }),
    staleTime: Infinity,
  })
  const headerData = useOptimisticData(headerQuery.data, {targets: [{queryKey: headerQueryKey}]})
  // Only this mount's own fetch outranks the props: a seed merely mirrors them, and a payload from
  // an earlier mount went unrefreshed for as long as the header was unmounted.
  return headerData.source === 'refetch' && headerData.fetchedBy === headerMountId ? headerData.issue : undefined
}
